import React from 'react';
import { Button, Tooltip } from 'antd';
import { EBookingLimitListingState } from 'types/bookingLimit';
import { isPausedByLimit } from './helpers';
import s from './s.module.less';

export interface IStatusPill {
    tone: 'green' | 'amber' | 'gray';
    label: string;
    tip: string;
    /** 混合状态下带人数：「Active listing 7」「Paused listing 1」 */
    count?: number;
}

/**
 * solo / 个人视角的 pill。直接吃后端的 listingState —— 之前是三个布尔位置参数，
 * 调用方每次都要从同一个枚举现场推一遍。混合状态只可能出现在 practice 视图，见 practicePills。
 */
export const ownPills = (
    state: EBookingLimitListingState | undefined,
    resetAt: string,
): IStatusPill[] => {
    if (state === EBookingLimitListingState.NOT_LISTED) {
        return [{
            tone: 'gray',
            label: 'Not listed',
            tip: 'You do not appear in Klarity Health marketplace search yet, so no new patients can book you through it.',
        }];
    }
    if (isPausedByLimit(state)) {
        return [{
            tone: 'amber',
            label: 'Paused listing',
            tip: `You reached the limit, so your profile is hidden from marketplace search until ${resetAt}.`,
        }];
    }
    if (state === EBookingLimitListingState.ACTIVE) {
        return [{ tone: 'green', label: 'Active listing', tip: 'You are live in marketplace search.' }];
    }
    return [];
};

/**
 * practice 视角的 pill。全员在线 / 全员暂停时只出一个；混合时两个并列且各带人数，
 * 避免「一个人被暂停」被读成「整个 practice 下线了」。
 */
export const practicePills = (
    activeCount: number,
    pausedCount: number,
    resetAt: string,
): IStatusPill[] => {
    if (pausedCount === 0) {
        return [{ tone: 'green', label: 'Active listing', tip: 'Every provider is live in marketplace search.' }];
    }
    if (activeCount === 0) {
        return [{
            tone: 'amber',
            label: 'Paused listing',
            tip: `Every provider reached the limit and is hidden from marketplace search until ${resetAt}.`,
        }];
    }
    return [
        {
            tone: 'green',
            label: 'Active listing',
            tip: `${activeCount} providers are live in marketplace search.`,
            count: activeCount,
        },
        {
            tone: 'amber',
            label: 'Paused listing',
            tip: `${pausedCount} provider${pausedCount === 1 ? '' : 's'} reached the limit and ${pausedCount === 1 ? 'is' : 'are'} hidden from marketplace search until ${resetAt}.`,
            count: pausedCount,
        },
    ];
};

interface Props {
    pills: IStatusPill[];
    intro: string;
    onViewHistory: () => void;
}

/**
 * 区块头：标题 + 状态 pill + View history。
 *
 * pill 回答的是 provider 最先关心的那个问题 ——「我现在还在不在线上」，
 * 所以它只随「是否已达上限」变化，不跟着草稿动；设置了上限本身不代表下架。
 */
const TONE_CLASS: Record<IStatusPill['tone'], string> = {
    green: s.pillGreen,
    amber: s.pillAmber,
    gray: s.pillGray,
};

const SectionHeader: React.FC<Props> = ({ pills, intro, onViewHistory }) => (
    <>
        <div className={s.hdr}>
            <div className={s.hdrLeft}>
                <h2 className={s.secTitle}>Patient booking limit</h2>
                {pills.map((p) => (
                    <Tooltip key={p.label} title={p.tip}>
                        <span className={`${s.pill} ${TONE_CLASS[p.tone]}`}>
                            <i className={s.pillDot} />
                            {p.label}
                            {typeof p.count === 'number' && <b className={s.pillCount}>{p.count}</b>}
                        </span>
                    </Tooltip>
                ))}
            </div>
            {/* 「View history」是动词，比名词更像一个可点的东西 */}
            <Button className={s.historyBtn} onClick={onViewHistory}>
                View history
            </Button>
        </div>
        <p className={s.sub}>{intro}</p>
    </>
);

export default SectionHeader;
